import { onlineManager } from "@tanstack/react-query";

/** Erro da API v1 (`{ error: { code, message, details? } }`) ou de rede. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let onUnauthorized: (() => void) | undefined;

/** Chamado quando qualquer requisição volta 401 (sessão expirada/revogada). */
export function setUnauthorizedHandler(handler: (() => void) | undefined) {
  onUnauthorized = handler;
}

type RequestOptions = {
  /** Vira o cabeçalho `Idempotency-Key` (criação de lançamento e transferência, M1-P1). */
  idempotencyKey?: string;
};

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  // Sem rede conhecida: falha antes de enviar, então é certo que nada foi salvo (P5). Sem fila offline.
  if (!onlineManager.isOnline()) throw new ApiError(0, "offline", "Sem conexão. Nada foi salvo.");
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      credentials: "same-origin",
      headers: {
        ...(body === undefined ? { accept: "application/json" } : { "content-type": "application/json" }),
        ...(options.idempotencyKey ? { "idempotency-key": options.idempotencyKey } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network_error", "Sem conexão com o servidor. Tente de novo.");
  }
  if (res.status === 204) return undefined as T;
  if (!res.ok) throw await toApiError(res);
  return (await res.json().catch(() => null)) as T;
}

/** Erro da resposta não-OK, no formato `{ error: { code, message } }` (401 avisa a sessão). */
export async function toApiError(res: Response): Promise<ApiError> {
  const data = (await res.json().catch(() => null)) as {
    error?: { code: string; message: string; details?: unknown };
  } | null;
  if (res.status === 401) onUnauthorized?.();
  const e = data?.error;
  return new ApiError(res.status, e?.code ?? "http_error", e?.message ?? `Erro ${res.status}`, e?.details);
}

export const api = {
  get: <T = unknown>(path: string) => request<T>("GET", path),
  post: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("POST", path, body ?? {}, options),
  patch: <T = unknown>(path: string, body: unknown) => request<T>("PATCH", path, body),
  put: <T = unknown>(path: string, body: unknown) => request<T>("PUT", path, body),
  delete: <T = unknown>(path: string) => request<T>("DELETE", path),
};
